const CONTROLLED_SEGMENTS = ['municipal', 'public_private', 'private', 'nursery_public', 'nursery_private', 'maluch_public', 'maluch_private'];
const ALL_DATA_SEGMENTS = [...CONTROLLED_SEGMENTS, 'unverified'];

export const ACTIVITY_BASE = '#0F7A5A';
export const NO_ACTIVITY = '#eef3f6';
export const ACTIVITY_WINDOW_DAYS = 365;

function activeDataSegments(selected = []) {
  const normalized = CONTROLLED_SEGMENTS.filter((segment) => selected.includes(segment));
  return normalized.length === CONTROLLED_SEGMENTS.length ? ALL_DATA_SEGMENTS : normalized;
}

function parseUtc(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
    ? `${text.replace(' ', 'T')}Z`
    : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function activityAgeDays(value, now = Date.now()) {
  const date = parseUtc(value);
  if (!date) return null;
  return Math.max(0, (now - date.getTime()) / 86400000);
}

export function activityFreshness(value, now = Date.now()) {
  const age = activityAgeDays(value, now);
  if (age === null || age >= ACTIVITY_WINDOW_DAYS) return 0;
  return Math.max(0, Math.min(1, 1 - age / ACTIVITY_WINDOW_DAYS));
}

function hexToRgb(hex) {
  const value = String(hex || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(value)) return [15, 122, 90];
  return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
}

function rgbToHex(rgb) {
  return `#${rgb.map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')).join('')}`;
}

export function activityColor(value) {
  const freshness = activityFreshness(value);
  if (freshness <= 0) return NO_ACTIVITY;
  const [r, g, b] = hexToRgb(ACTIVITY_BASE);
  // Recent contact is saturated. At 12 months the marker almost disappears into the map.
  const strength = 0.12 + 0.88 * freshness;
  return rgbToHex([
    255 - (255 - r) * strength,
    255 - (255 - g) * strength,
    255 - (255 - b) * strength,
  ]);
}

export function activityTextColor(value) {
  return activityFreshness(value) > 0.52 ? '#ffffff' : '#173b5d';
}

export function activityDateText(value) {
  const date = parseUtc(value);
  if (!date || activityFreshness(value) <= 0) return 'Brak kontaktu ≤ 12 mies.';
  const age = activityAgeDays(value);
  if (age < 1) return 'dzisiaj';
  if (age < 2) return 'wczoraj';
  if (age < 31) return `${Math.floor(age)} dni temu`;
  if (age < 61) return '1 mies. temu';
  if (age < 365) return `${Math.floor(age / 30.44)} mies. temu`;
  return 'ponad rok temu';
}

export function combineActivityBuckets(buckets = {}, selectedSegments = []) {
  let activeCount = 0;
  let latest = null;
  activeDataSegments(selectedSegments).forEach((segment) => {
    const bucket = buckets?.[segment];
    if (!bucket) return;
    activeCount += Number(bucket.active_count || 0);
    const candidate = String(bucket.latest_activity_at || '').trim();
    if (candidate && (!latest || candidate > latest)) latest = candidate;
  });
  if (activityFreshness(latest) <= 0) latest = null;
  return { active_count: activeCount, latest_activity_at: latest };
}

export function activitySummaryForItems(items = [], scope = 'commune') {
  let activeCount = 0;
  let latest = null;
  if (scope === 'commune') {
    items.forEach((item) => {
      const candidate = String(item?.last_activity_at || '').trim();
      if (activityFreshness(candidate) <= 0) return;
      activeCount += 1;
      if (!latest || candidate > latest) latest = candidate;
    });
  } else {
    items.forEach((item) => {
      activeCount += Number(item?.activity_summary?.active_count || 0);
      const candidate = String(item?.activity_summary?.latest_activity_at || '').trim();
      if (candidate && activityFreshness(candidate) > 0 && (!latest || candidate > latest)) latest = candidate;
    });
  }
  return { active_count: activeCount, latest_activity_at: latest };
}

export function activityValueText(item, scope = 'commune') {
  const at = scope === 'commune' ? item?.last_activity_at : item?.activity_summary?.latest_activity_at;
  if (activityFreshness(at) <= 0) return 'brak kontaktu ≤ 12 mies.';
  if (scope === 'commune') return activityDateText(at);
  const count = Number(item?.activity_summary?.active_count || 0);
  return `${activityDateText(at)} · ${count} ${count === 1 ? 'placówka' : 'placówek'}`;
}

export function activityTooltip(item, scope = 'commune') {
  if (!item) return 'Brak danych';
  const text = activityValueText(item, scope);
  if (scope !== 'commune') return `${item.name} — ostatnia aktywność ${text}`;
  const subject = String(item?.last_activity_subject || '').trim();
  const type = String(item?.last_activity_type || '').trim();
  return `${item.name} — ostatnia aktywność: ${text}${type ? ` · ${type}` : ''}${subject ? ` · ${subject}` : ''}`;
}

export function sortByActivity(items = [], scope = 'commune') {
  return [...items].sort((a, b) => {
    const av = scope === 'commune' ? String(a?.last_activity_at || '') : String(a?.activity_summary?.latest_activity_at || '');
    const bv = scope === 'commune' ? String(b?.last_activity_at || '') : String(b?.activity_summary?.latest_activity_at || '');
    const af = activityFreshness(av);
    const bf = activityFreshness(bv);
    if (bf !== af) return bf - af;
    if (scope !== 'commune') {
      const ac = Number(a?.activity_summary?.active_count || 0);
      const bc = Number(b?.activity_summary?.active_count || 0);
      if (bc !== ac) return bc - ac;
    }
    return String(a?.name || '').localeCompare(String(b?.name || ''), 'pl');
  });
}
